import {showNotification} from "@/actions";
import {Action} from "@reduxjs/toolkit";
import {defineMessage} from "react-intl";
import {CellHyperlinkValue, CellValue, Style} from "exceljs";

export const MAXIMUM_ADDRESS_BOOK_ENTRIES = 10_000;
export const EXCEL_MAX_ROWS = 1_048_576;

export abstract class ExcelFileErrorBase extends Error {
    private vars: Record<string, any>;

    constructor(message: string, vars?: Record<string, any>) {
        super(message);
        this.vars = vars;
    }

    get action(): Action {
        // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
        return showNotification(defineMessage({id: this.message}), "error", this.vars);
    }
}

export class CorruptedExcelFileError extends ExcelFileErrorBase {
    constructor() {
        super("excel_file.file_errors.corrupt_file");
    }
}

export class NoSheetInExcelFileError extends ExcelFileErrorBase {
    constructor() {
        super("excel_file.file_errors.no_sheet");
    }
}

export class TooManyAddressBookEntriesError extends ExcelFileErrorBase {
    constructor() {
        super("excel_file.file_errors.too_many_address_book_entries", {max: MAXIMUM_ADDRESS_BOOK_ENTRIES});
    }
}

export enum ExcelFileError {
    CORRUPT_EXCEL_FILE = "excel_file.file_errors.corrupt_file",
    NO_SHEET = "excel_file.file_errors.no_sheet",
    TOO_MANY_ENTRIES = "excel_file.file_errors.too_many_address_book_entries",
}

/**
 * Making the cell values 'text' format for that column.
 */
export const TextFormat: Partial<Style> = {numFmt: "@"};

/**
 * Remove the hyperlink / link / 'mailto:' from the email address cell value
 *
 * Do not use 'toString()' on the cell value.
 *
 * @param email The cell value of the email address
 * @returns The email address without link and format
 */
export const removeExcelEmailLink = (email: string | CellValue): string => {
    if (email === undefined || email === null) return email as unknown as string;

    if (typeof email === "string") {
        if (email.startsWith('HYPERLINK("mailto:')) {
            return email.substring(18, email.length - 2);
        }
        if (email.startsWith("mailto:")) {
            return email.substring(7);
        }
        return email;
    }

    return (email as CellHyperlinkValue).text;
};
